import React from 'react';
import { ComponentCondition, ProcurementStatus } from '../../types/bom';

export interface ConditionBadgeProps {
  condition?: ComponentCondition;
  status?: ProcurementStatus;
  size?: 'sm' | 'default';
  className?: string;
}

export const ConditionBadge: React.FC<ConditionBadgeProps> = ({
  condition,
  status,
  size = 'default',
  className = '',
}) => {
  const sizeClasses = size === 'sm' ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-xs';

  if (condition) {
    const conditionConfig: Record<
      ComponentCondition,
      { label: string; styles: string; title: string }
    > = {
      'OEM NOS': {
        label: 'OEM NOS',
        styles: 'bg-[#1e3a8a] text-[#eff6ff] border-[#3b82f6]',
        title: 'Original Equipment Manufacturer - New Old Stock (Authentic factory era, unused)',
      },
      'Reproduction': {
        label: 'REPRO',
        styles: 'bg-[#1e293b] text-[#f1f5f9] border-[#475569]',
        title: 'Reproduction (Modern aftermarket replication to factory specs)',
      },
      'Salvage': {
        label: 'SALVAGE',
        styles: 'bg-[#78350f] text-[#fef3c7] border-[#d97706]',
        title: 'Salvage / Donor Core (Original factory component harvested from donor vehicle)',
      },
      'Remanufactured': {
        label: 'REMAN',
        styles: 'bg-[#064e3b] text-[#ecfdf5] border-[#10b981]',
        title: 'Remanufactured (Restored and rebuilt to factory tolerances with new wear items)',
      },
    };

    const cfg = conditionConfig[condition] || {
      label: condition,
      styles: 'bg-shop-panel text-shop-light border-shop-border',
      title: condition,
    };

    return (
      <span
        title={cfg.title}
        className={`inline-flex items-center justify-center font-mono font-bold tracking-wider uppercase rounded-sm border select-none ${cfg.styles} ${sizeClasses} ${className}`}
      >
        {cfg.label}
      </span>
    );
  }

  if (status) {
    const statusConfig: Record<
      ProcurementStatus,
      { label: string; styles: string; title: string }
    > = {
      'Needed': {
        label: 'NEEDED',
        styles: 'bg-[#78350f] text-[#fef3c7] border-[#f59e0b]',
        title: 'Required part; sourcing search active',
      },
      'Sourced': {
        label: 'SOURCED',
        styles: 'bg-[#1e3a8a] text-[#dbeafe] border-[#3b82f6]',
        title: 'Ordered, committed funds or in transit',
      },
      'Installed': {
        label: 'INSTALLED',
        styles: 'bg-[#064e3b] text-[#d1fae5] border-[#10b981]',
        title: 'Fitted, torqued to specification, and validated functional',
      },
    };

    const cfg = statusConfig[status] || {
      label: status,
      styles: 'bg-shop-panel text-shop-light border-shop-border',
      title: status,
    };

    return (
      <span
        title={cfg.title}
        className={`inline-flex items-center justify-center font-mono font-bold tracking-wider uppercase rounded-sm border select-none ${cfg.styles} ${sizeClasses} ${className}`}
      >
        {cfg.label}
      </span>
    );
  }

  return null;
};
